<script setup>
import { ref, onMounted, computed } from 'vue'
import { fetchServicesData } from '@/services/api'
import DynamicHero from '../components/DynamicHero.vue'
import HowWeWorks from '../components/HowWeWorks.vue'

// Fallback images
import bgHero from '@/assets/img/bg-services.png'
import tvCommercial from '@/assets/img/services/our-services-1.png'
import companyProfile from '@/assets/img/services/our-services-2.png'
import animationVideo from '@/assets/img/services/our-services-3.png'
import campaignVideo from '@/assets/img/services/our-services-4.png'
import bgCTA from '@/assets/img/bg-creativity-unlocks-opportunities.png'

const slides = ref([])
const services = ref([])
const isLoading = ref(true)

// Map service title to route path
const getRoutePath = (title) => {
    const routeMap = {
        'TV Commmercial': '/services/tv-commercial-video',
        'TV Commercial': '/services/tv-commercial-video',
        'Video Animation': '/services/animation-video',
        'Animation Video': '/services/animation-video',
        'Company Profile': '/services/company-profile',
        'Video Campaigns': '/services/campaign-video',
        'Campaign Video': '/services/campaign-video'
    }
    return routeMap[title] || '/services'
}

// Get fallback image by service title
const getFallbackImage = (title) => {
    const imageMap = {
        'TV Commmercial': tvCommercial,
        'TV Commercial': tvCommercial,
        'Video Animation': animationVideo,
        'Animation Video': animationVideo,
        'Company Profile': companyProfile,
        'Video Campaigns': campaignVideo,
        'Campaign Video': campaignVideo
    }
    return imageMap[title] || companyProfile
}

onMounted(async () => {
    try {
        const data = await fetchServicesData()
        slides.value = data.slides || []
        services.value = data.services || []
    } catch (error) {
        console.error('Failed to load services data:', error)
    } finally {
        isLoading.value = false
    }
})
</script>

<template>
    <DynamicHero :slides="slides" :fallbackBg="bgHero" fallbackTitle="Where Creativity Meets Visual Impact"
        fallbackDescription="  From concept to final delivery, we deliver high-quality video production services designed to communicate, inspire, and create lasting impressions."
        fallbackButtonText="Consultation Now" fallbackButtonLink="/services" breadcrumbLabel="Our Services" />

    <!-- Our Services -->
    <section class="wrapper px-0 bg-black">
        <div class="container pt-10 pb-12 pt-md-14 pb-md-16 text-center">
            <div class="row">
                <div class="mx-auto text-white">
                    <h1 class="display-1 mb-3 text-white">
                        Our <span style="color: #ffae0b;">Services</span>
                    </h1>
                    <p class="lead">
                        SmilePictures stands out as a video production house with high production quality, supported by
                        a
                        team of experienced professionals and state-of-the-art equipment. We combine creativity and
                        innovation to understand and realize clients' visions, while offering complete solutions at
                        competitive prices. Our dedication to client service and ability to collaborate with the best
                        talent makes SmilePictures a trusted choice in the industry.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- Dynamic Service Cards -->
    <template v-for="(service, index) in services" :key="service.id">
        <!-- Even index: Image left, Text right (bg-dark) -->
        <section v-if="index % 2 === 0" class="wrapper px-0 bg-dark">
            <div class="container pt-10 pb-12 pt-md-14 pb-md-16">
                <div class="col">
                    <div class="card card-transparent text-white border-0">
                        <div class="row g-0 align-items-center">
                            <div class="col-md-4">
                                <img :src="service.image" :alt="service.title" class="img-fluid rounded">
                            </div>
                            <div class="col-md-8">
                                <div class="card-body text-start">
                                    <h5 class="card-title text-secondary">{{ service.title }}</h5>
                                    <p class="card-text">{{ service.description }}</p>
                                    <router-link :to="`/services/${service.slug}`" class="learn-more-link">
                                        Learn More <i class="uil uil-arrow-up-right"></i>
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Odd index: Text left, Image right (bg-black) -->
        <section v-else class="wrapper px-0 bg-black">
            <div class="container pt-10 pb-12 pt-md-14 pb-md-16">
                <div class="col">
                    <div class="card card-transparent text-white border-0">
                        <div class="row g-0 align-items-center">
                            <div class="col-md-8">
                                <div class="card-body text-start">
                                    <h5 class="card-title text-secondary">{{ service.title }}</h5>
                                    <p class="card-text">{{ service.description }}</p>
                                    <router-link :to="`/services/${service.slug}`" class="learn-more-link">
                                        Learn More <i class="uil uil-arrow-up-right"></i>
                                    </router-link>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <img :src="service.image" :alt="service.title" class="img-fluid rounded">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </template>

    <!-- Fallback static cards when no API data -->
    <template v-if="!isLoading && services.length === 0">
        <!-- Card 1: Company Profile -->
        <section class="wrapper px-0 bg-dark">
            <div class="container pt-10 pb-12 pt-md-14 pb-md-16">
                <div class="col">
                    <div class="card card-transparent text-white border-0">
                        <div class="row g-0 align-items-center">
                            <div class="col-md-4">
                                <img :src="companyProfile" alt="Company Profile" class="img-fluid rounded">
                            </div>
                            <div class="col-md-8">
                                <div class="card-body text-start">
                                    <h5 class="card-title text-secondary">Company Profile</h5>
                                    <p class="card-text">
                                        We produce professional company profile videos that showcase your brand story
                                        effectively. Engage your audience and elevate your company's image
                                        with visually compelling content.
                                    </p>
                                    <router-link to="/services/company-profile" class="learn-more-link">
                                        Learn More <i class="uil uil-arrow-up-right"></i>
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Card 2: Campaign Video -->
        <section class="wrapper px-0 bg-black">
            <div class="container pt-10 pb-12 pt-md-14 pb-md-16">
                <div class="col">
                    <div class="card card-transparent text-white border-0">
                        <div class="row g-0 align-items-center">
                            <div class="col-md-8">
                                <div class="card-body text-start">
                                    <h5 class="card-title text-secondary">Campaign Video</h5>
                                    <p class="card-text">
                                        We develop integrated video campaigns for brands, social programs, and
                                        institutions.
                                        From concept to execution, each piece is tailored to achieve relevance,
                                        engagement,
                                        and measurable impact.
                                    </p>
                                    <router-link to="/services/campaign-video" class="learn-more-link">
                                        Learn More <i class="uil uil-arrow-up-right"></i>
                                    </router-link>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <img :src="campaignVideo" alt="Campaign Video" class="img-fluid rounded">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Card 3: Animation Video -->
        <section class="wrapper px-0 bg-dark">
            <div class="container pt-10 pb-12 pt-md-14 pb-md-16">
                <div class="col">
                    <div class="card card-transparent text-white border-0">
                        <div class="row g-0 align-items-center">
                            <div class="col-md-4">
                                <img :src="animationVideo" alt="Animation Video" class="img-fluid rounded">
                            </div>
                            <div class="col-md-8">
                                <div class="card-body text-start">
                                    <h5 class="card-title text-secondary">Animation Video</h5>
                                    <p class="card-text">
                                        We produce dynamic and engaging animation content, including motion graphics and
                                        explainer videos. Designed to simplify complex ideas and deliver messages
                                        clearly
                                        and creatively.
                                    </p>
                                    <router-link to="/services/animation-video" class="learn-more-link">
                                        Learn More <i class="uil uil-arrow-up-right"></i>
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Card 4: TV Commercial Video -->
        <section class="wrapper px-0 bg-black">
            <div class="container pt-10 pb-12 pt-md-14 pb-md-16">
                <div class="col">
                    <div class="card card-transparent text-white border-0">
                        <div class="row g-0 align-items-center">
                            <div class="col-md-8">
                                <div class="card-body text-start">
                                    <h5 class="card-title text-secondary">TV Commercial Video</h5>
                                    <p class="card-text">
                                        We create high-quality commercial videos with strong storytelling
                                        and impactful visuals. Each concept is crafted to strengthen your brand
                                        message and capture audience attention effectively.
                                    </p>
                                    <router-link to="/services/tv-commercial-video" class="learn-more-link">
                                        Learn More <i class="uil uil-arrow-up-right"></i>
                                    </router-link>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <img :src="tvCommercial" alt="TV Commercial" class="img-fluid rounded">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </template>

    <HowWeWorks />

    <section class="wrapper px-0" :style="{
        backgroundImage: `url(${bgCTA})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
    }">
        <div class="container pt-10 pb-12 pt-md-14 pb-md-16 text-center">
            <div class="row">
                <div class="mx-auto text-white">
                    <h4 class="display-4 mb-3 text-white">
                        In the world of business, creativity is the key that unlocks doors to infinite opportunities.
                    </h4>
                </div>
            </div>
        </div>
    </section>
</template>

<style>
.card-transparent {
    background-color: transparent !important;
}

.card-body .learn-more-link {
    position: relative;
    font-weight: 500;
    color: #ffae0b;
    text-decoration: none;
}

.card-body .learn-more-link::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    height: 2px;
    background-color: #ffae0b;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
}

.card-body .learn-more-link:hover::after {
    transform: scaleX(1);
}

.card-title {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
}

.card-text {
    font-size: 1rem;
}
</style>
